/**
 * The map home's WORLD: where every node sits, in one fixed coordinate space.
 *
 * The map is authored at a fixed 1200×800 and scaled as a whole by the camera (`map-camera.ts`), the same
 * way the genome is authored at 343px and scaled — so nothing here measures the screen, and a position is
 * exact at every size. Mirrored 1:1 on iOS (`MapLayout.swift`) and Android (`MapLayout.kt`); every constant
 * here is theirs too, and the parity tests pin the numbers.
 *
 * The regions are the ones agreed with design (WhatsApp, 2026-09-11):
 *
 *            ┌────────────── brief (top) ──────────────┐
 *   channels │            agents around the            │ outreach
 *     in     │              Maestro (centre)           │  (right)
 *   (left)   │                                         │
 *            │        connectors (bottom row)          │
 *            └── files · tasks · goals · apps · msgs ──┘  ← the buckets, on the bottom edge under the tiles
 *
 * Pure and import-free so it is unit-testable and portable.
 */

export const WORLD_W = 1200;
export const WORLD_H = 800;
export const WORLD_CENTER = { x: WORLD_W / 2, y: WORLD_H / 2 };

/** The agents' ring radius around the Maestro, and the most nodes it holds before the rest are folded. */
export const AGENT_RING_R = 120;
export const MAX_AGENT_NODES = 10;

/** Column / row anchors for the four outer regions. */
export const CHANNELS_IN_X = 170;
export const OUTREACH_X = 1030;
/** The connected TOOLS (Connectors & MCP) sit right under the agents ring, wired to the Maestro. */
export const CONNECTORS_Y = 600;
/** The morning meeting — a CARD on the map, 560×170 world units — sits right above the ring, wired to the Maestro. */
/** The meeting card's centre: its bottom (y + h/2) must clear the ring's top agent (WORLD_CENTER.y − AGENT_RING_R − r). */
export const BRIEF_Y = 156;
export const BRIEF_CARD = { w: 560, h: 184 };
export const BUCKETS_Y = 745;
/** The vertical band the two side columns spread over, and the horizontal band the bottom row does. */
export const SIDE_BAND = { top: 150, bottom: 650 };
export const BOTTOM_BAND = { left: 300, right: 900 };
/** The most nodes a side column / bottom row draws before folding the rest into a "+N" node. */
export const MAX_SIDE_NODES = 8;
export const MAX_BOTTOM_NODES = 8;

/** The slug of the "connect more" node at the end of the connectors row — a button, not a tool. */
export const CONNECT_MORE_SLUG = '+connect';

/**
 * The OUTREACH channels — the ways Symphony can reach someone on the user's behalf. Fixed, in this order,
 * whether or not the account has connected each (an unconnected one is drawn dimmed and its card offers to
 * connect it). Product list, 2026-09-11.
 */
export const OUTREACH_CHANNELS = [
  { slug: 'email', label: 'Email' },
  { slug: 'whatsapp', label: 'WhatsApp' },
  { slug: 'sms', label: 'SMS' },
  { slug: 'imessage', label: 'iMessage' },
  { slug: 'phone', label: 'Phone' },
  { slug: 'instagram', label: 'Instagram DM' },
  { slug: 'x', label: 'X DM' },
  { slug: 'messenger', label: 'Messenger' },
] as const;
export type OutreachSlug = (typeof OUTREACH_CHANNELS)[number]['slug'];

export type MapRegion = 'centre' | 'agents' | 'brief' | 'channelsIn' | 'connectors' | 'outreach' | 'buckets' | 'spaces';
/** The opening frame: the meeting card and the agents ring in one view (the "landing" of the entry choreography). */
export type MapFrame = MapRegion | 'landing';

/** The five destinations at the foot — one per entry tile, in the tiles' own order. */
export const BUCKETS = ['tasks', 'goals', 'routines', 'files', 'apps', 'messages'] as const;
export type BucketId = (typeof BUCKETS)[number];

export interface MapNode {
  id: string;
  region: MapRegion;
  x: number;
  y: number;
  /** What to draw / say. Agents carry their name, connectors their toolkit, buckets their tile key. */
  label: string;
  /** For connectors and channels: the toolkit slug, so the renderer can pick a logo. */
  slug?: string;
  /** A folded "+N" node stands for this many hidden siblings. */
  folded?: number;
  /** The connector's own logo, when the connectors screen has one (the icon it shows). */
  logoUrl?: string;
  /** A folded "+N" node: the names it stands for, for its card. */
  hidden?: string[];
  /** A Space district: its emoji and the dashboard route it opens. */
  icon?: string;
  href?: string;
  /** Channels and connectors: is this one actually wired up? An unconnected outreach channel is drawn
   *  dimmed with a dotted spoke, and its card offers to connect it. Absent = not applicable. */
  connected?: boolean;
  /** The "connect more" affordance at the end of the connectors row. */
  action?: 'connect';
}

export interface MapEdge {
  from: string;
  to: string;
  /** Solid edges are the standing wiring (a channel into the Maestro, the Maestro out to a channel);
   *  dashed ones are occasional (a connector the agents call on). */
  style: 'solid' | 'dashed';
}

export interface MapWorld {
  nodes: MapNode[];
  edges: MapEdge[];
}

export interface MapInputs {
  agents: { id: string; name: string }[];
  /** Inbound communication channels the account has connected (WhatsApp, Gmail, Slack, SMS, a calendar…). */
  channelsIn: { slug: string; label: string }[];
  /** Every connected TOOL: the Connectors & MCP page's connected connectors plus its MCP servers. */
  connectors: { slug: string; label: string; logoUrl?: string }[];
  /** The outreach channels, each with whether it is connected. Callers usually pass `outreachChannels(...)`. */
  outreach: { slug: string; label: string; connected: boolean }[];
  /** The account's Spaces — the DISTRICTS along the bottom edge that a task, goal, file or app lands in.
   *  Every agent works in all of them, so a Space is never near the agents: it is where the work falls. */
  spaces?: { id: string; slug: string; name: string; icon?: string }[];
}

/** The district for work that belongs to no Space. */
export function spaceNodeId(workspaceId: string): string {
  return `space:${workspaceId}`;
}
/** The districts row: one plate per Space plus "everything else", spread along the bottom edge. */
export const SPACES_Y = 745;
export const MAX_SPACE_NODES = 5;

export const MAESTRO_ID = 'maestro';
export const BRIEF_ID = 'brief';

export function agentNodeId(agentId: string): string {
  return `agent:${agentId}`;
}
export function channelInNodeId(slug: string): string {
  return `in:${slug}`;
}
export function connectorNodeId(slug: string): string {
  return `tool:${slug}`;
}
export function outreachNodeId(slug: string): string {
  return `out:${slug}`;
}
export function bucketNodeId(bucket: BucketId): string {
  return `bucket:${bucket}`;
}

/** The agents' ring angles: evenly spaced, 0° at the top, clockwise — the genome's own convention. */
export function agentAngles(count: number): number[] {
  if (count <= 0) return [];
  const step = 360 / count;
  return Array.from({ length: count }, (_, i) => (i * step) % 360);
}

export function polar(cx: number, cy: number, r: number, angleDeg: number): { x: number; y: number } {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r };
}

/** N positions spread evenly across a band, centred: one item sits in the middle, two at the thirds… */
export function spread(count: number, from: number, to: number): number[] {
  if (count <= 0) return [];
  if (count === 1) return [(from + to) / 2];
  const step = (to - from) / (count + 1);
  return Array.from({ length: count }, (_, i) => from + step * (i + 1));
}

/** Keep the first `max - 1` items and fold the rest into one "+N" entry when there are more than `max`. */
export function fold<T extends { slug: string; label: string }>(
  items: T[],
  max: number,
): (T | { slug: string; label: string; folded: number; hidden: string[] })[] {
  if (items.length <= max) return items;
  const kept = items.slice(0, max - 1);
  const rest = items.slice(kept.length);
  return [...kept, { slug: '+more', label: `+${rest.length}`, folded: rest.length, hidden: rest.map((i) => i.label) }];
}

export function buildWorld(inputs: MapInputs): MapWorld {
  const nodes: MapNode[] = [];
  const edges: MapEdge[] = [];

  nodes.push({ id: MAESTRO_ID, region: 'centre', x: WORLD_CENTER.x, y: WORLD_CENTER.y, label: 'Maestro' });
  nodes.push({ id: BRIEF_ID, region: 'brief', x: WORLD_CENTER.x, y: BRIEF_Y, label: 'Morning brief' });
  // The meeting is wired to the Maestro: it is where the day's plan comes from.
  edges.push({ from: BRIEF_ID, to: MAESTRO_ID, style: 'solid' });

  const agents = inputs.agents.slice(0, MAX_AGENT_NODES);
  agentAngles(agents.length).forEach((angle, i) => {
    const p = polar(WORLD_CENTER.x, WORLD_CENTER.y, AGENT_RING_R, angle);
    nodes.push({ id: agentNodeId(agents[i].id), region: 'agents', x: p.x, y: p.y, label: agents[i].name });
  });

  const channelsIn = fold(inputs.channelsIn, MAX_SIDE_NODES);
  spread(channelsIn.length, SIDE_BAND.top, SIDE_BAND.bottom).forEach((y, i) => {
    const c = channelsIn[i];
    const id = channelInNodeId(c.slug);
    nodes.push({
      id,
      region: 'channelsIn',
      x: CHANNELS_IN_X,
      y,
      label: c.label,
      slug: c.slug,
      ...('folded' in c ? { folded: c.folded, hidden: c.hidden } : {}),
    });
    edges.push({ from: id, to: MAESTRO_ID, style: 'solid' });
  });

  const outreach = fold(inputs.outreach, MAX_SIDE_NODES);
  spread(outreach.length, SIDE_BAND.top, SIDE_BAND.bottom).forEach((y, i) => {
    const c = outreach[i];
    const id = outreachNodeId(c.slug);
    const connected = 'connected' in c ? c.connected : true;
    nodes.push({
      id,
      region: 'outreach',
      x: OUTREACH_X,
      y,
      label: c.label,
      slug: c.slug,
      connected,
      ...('folded' in c ? { folded: c.folded, hidden: c.hidden } : {}),
    });
    // A connected channel is a standing wire; an unconnected one is drawn dotted (see the renderer).
    edges.push({ from: MAESTRO_ID, to: id, style: connected ? 'solid' : 'dashed' });
  });

  // The connected tools, then the "connect more" button as the row's last node — a fixed slot, never folded.
  const tools = fold(inputs.connectors, MAX_BOTTOM_NODES - 1);
  const row = [...tools, { slug: CONNECT_MORE_SLUG, label: '+', action: 'connect' as const }];
  spread(row.length, BOTTOM_BAND.left, BOTTOM_BAND.right).forEach((x, i) => {
    const c = row[i];
    const id = connectorNodeId(c.slug);
    if ('action' in c) {
      nodes.push({ id, region: 'connectors', x, y: CONNECTORS_Y, label: c.label, slug: c.slug, action: 'connect' });
      return;
    }
    nodes.push({
      id,
      region: 'connectors',
      x,
      y: CONNECTORS_Y,
      label: c.label,
      slug: c.slug,
      connected: true,
      ...('folded' in c ? { folded: c.folded, hidden: c.hidden } : {}),
      ...('logoUrl' in c && c.logoUrl ? { logoUrl: c.logoUrl } : {}),
    });
    edges.push({ from: id, to: MAESTRO_ID, style: 'solid' });
  });

  spread(BUCKETS.length, 120, WORLD_W - 120).forEach((x, i) => {
    nodes.push({ id: bucketNodeId(BUCKETS[i]), region: 'buckets', x, y: BUCKETS_Y, label: BUCKETS[i] });
  });
  // The Space districts, on the same bottom edge as the (never drawn) buckets: a particle for work that
  // belongs to a Space lands in its district; the rest in "everything else".
  // One plate per Space, no catch-all: work that belongs to no Space lands in its bucket (off the map's
  // bottom edge, toward the tile) exactly as before — a plate that opens nothing is not a district.
  const spaces = (inputs.spaces ?? []).slice(0, MAX_SPACE_NODES);
  const districts = spaces.map((s) => ({ id: spaceNodeId(s.id), label: s.name, icon: s.icon, href: `/workspace/${s.slug}` }));
  if (districts.length === 0) return { nodes, edges };
  // Plates are 168 wide (HomeMap DISTRICT.w) with a 6-unit halo: six of them at 196 apart span 1148.
  const dx = Math.min(200, (WORLD_W - 220) / Math.max(1, districts.length - 1));
  const x0 = WORLD_CENTER.x - (dx * (districts.length - 1)) / 2;
  for (let i = 0; i < districts.length; i++) {
    const d = districts[i];
    nodes.push({ id: d.id, region: 'spaces', x: x0 + i * dx, y: SPACES_Y, label: d.label, icon: d.icon, href: d.href });
  }

  return { nodes, edges };
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** The world rectangle a region occupies — what the camera frames when it zooms to a region. */
export function regionRect(region: MapFrame): Rect {
  switch (region) {
    case 'landing':
      // The meeting card and the Maestro under it — the card lands LARGE (about half the pane's width on a
      // wide pane), with the top of the ring in view; the ring's lower half and the tools row are one
      // scroll away. A taller region (card + whole ring) framed the entire world on a wide, short pane and
      // the card came in small.
      return {
        x: WORLD_CENTER.x - BRIEF_CARD.w / 2 - 20,
        y: BRIEF_Y - BRIEF_CARD.h / 2 - 20,
        w: BRIEF_CARD.w + 40,
        h: WORLD_CENTER.y + 40 - (BRIEF_Y - BRIEF_CARD.h / 2 - 20),
      };
    case 'brief':
      return { x: WORLD_CENTER.x - BRIEF_CARD.w / 2 - 20, y: BRIEF_Y - BRIEF_CARD.h / 2 - 20, w: BRIEF_CARD.w + 40, h: BRIEF_CARD.h + 40 };
    case 'centre':
    case 'agents':
      return { x: 380, y: 240, w: 440, h: 320 };
    case 'channelsIn':
      return { x: 40, y: 150, w: 360, h: 500 };
    case 'outreach':
      return { x: 800, y: 150, w: 360, h: 500 };
    case 'connectors':
      return { x: 240, y: 540, w: 720, h: 130 };
    case 'buckets':
    case 'spaces':
      return { x: 40, y: 680, w: 1120, h: 120 };
  }
}

export const WORLD_RECT: Rect = { x: 0, y: 0, w: WORLD_W, h: WORLD_H };

/** A smooth cubic between two nodes, bowing toward the world centre so edges read as flows, not spokes. */
export function edgePath(from: { x: number; y: number }, to: { x: number; y: number }): string {
  const dx = to.x - from.x;
  const c1 = { x: from.x + dx * 0.45, y: from.y };
  const c2 = { x: to.x - dx * 0.45, y: to.y };
  return `M ${r(from.x)} ${r(from.y)} C ${r(c1.x)} ${r(c1.y)}, ${r(c2.x)} ${r(c2.y)}, ${r(to.x)} ${r(to.y)}`;
}

function r(n: number): number {
  return Math.round(n * 10) / 10;
}
